import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../utils/api';
import { FiX, FiClock, FiMapPin, FiUser, FiBell, FiEdit, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const createDefaultNotificationSettings = () => ({
  enabled: true,
  notificationType: 'call',
  notificationTime: 15,
  customMessage: '',
  preCallSms: {
    enabled: false,
    minutesBeforeCall: 15
  }
});

const normalizeNotificationSettings = (settings) => {
  const defaults = createDefaultNotificationSettings();
  return {
    ...defaults,
    ...(settings || {}),
    preCallSms: {
      ...defaults.preCallSms,
      ...(settings?.preCallSms || {})
    }
  };
};

const toDateTimeLocal = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const buildDefaultEventForm = (selectedDate, sources = []) => {
  const baseStart = selectedDate ? new Date(selectedDate) : new Date();
  baseStart.setSeconds(0, 0);

  const roundedStart = new Date(baseStart);
  if (roundedStart.getMinutes() !== 0) {
    roundedStart.setMinutes(0, 0, 0);
    roundedStart.setHours(roundedStart.getHours() + 1);
  }

  const defaultSource = sources[0] || null;
  const defaultCalendar = defaultSource?.calendars?.[0] || null;

  return {
    title: '',
    description: '',
    location: '',
    startTime: toDateTimeLocal(roundedStart),
    endTime: toDateTimeLocal(new Date(roundedStart.getTime() + 60 * 60 * 1000)),
    provider: defaultSource?.provider || '',
    accountId: defaultSource?.accountId || '',
    calendarId: defaultCalendar?.id || ''
  };
};

const buildEventFormFromEvent = (event) => ({
  title: event?.title || '',
  description: event?.description || '',
  location: event?.location || '',
  startTime: toDateTimeLocal(event?.startTime),
  endTime: toDateTimeLocal(event?.endTime),
  provider: event?.calendarProvider || '',
  accountId: event?.accountId ? String(event.accountId) : '',
  calendarId: event?.calendarId || ''
});

const EventModal = ({ event, selectedDate, isOpen, onClose, onEventUpdate }) => {
  const queryClient = useQueryClient();
  const [isEditingNotifications, setIsEditingNotifications] = React.useState(false);
  const [isEditingMeeting, setIsEditingMeeting] = React.useState(false);
  const [notificationSettings, setNotificationSettings] = React.useState(() =>
    normalizeNotificationSettings(event?.notificationSettings)
  );
  const [eventForm, setEventForm] = React.useState(() => buildDefaultEventForm(selectedDate, []));

  const { data: sourcesData, isLoading: isLoadingSources } = useQuery(
    'calendarSources',
    async () => {
      const response = await api.get('/calendar/sources');
      return response.data;
    },
    {
      enabled: isOpen
    }
  );

  const writableSources = React.useMemo(
    () => (sourcesData?.sources || []).filter((source) => source.writable && source.calendars.length > 0),
    [sourcesData]
  );

  const currentSource = React.useMemo(() => {
    const accountId = event ? String(event.accountId || '') : eventForm.accountId;
    return (sourcesData?.sources || []).find((source) => source.accountId === accountId) || null;
  }, [event, eventForm.accountId, sourcesData]);

  const selectedSource = React.useMemo(() => {
    return writableSources.find((source) => source.accountId === eventForm.accountId) || null;
  }, [eventForm.accountId, writableSources]);

  const selectedSourceCalendars = selectedSource?.calendars || [];
  const canEditExistingMeeting = event ? !!currentSource?.writable : !!sourcesData?.hasWritableSource;

  React.useEffect(() => {
    setNotificationSettings(normalizeNotificationSettings(event?.notificationSettings));
    setIsEditingNotifications(false);
    setIsEditingMeeting(false);
  }, [event, isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (event) {
      setEventForm(buildEventFormFromEvent(event));
      return;
    }

    setEventForm((current) => {
      const next = buildDefaultEventForm(selectedDate, writableSources);
      if (!current.accountId && !current.calendarId && !current.title && !current.description && !current.location) {
        return next;
      }

      if (!writableSources.some((source) => source.accountId === current.accountId)) {
        return next;
      }

      const currentSourceMatch = writableSources.find((source) => source.accountId === current.accountId);
      const hasCurrentCalendar = currentSourceMatch?.calendars?.some((calendar) => calendar.id === current.calendarId);

      return {
        ...current,
        provider: currentSourceMatch?.provider || current.provider,
        calendarId: hasCurrentCalendar ? current.calendarId : currentSourceMatch?.calendars?.[0]?.id || ''
      };
    });
  }, [event, isOpen, selectedDate, writableSources]);

  const updateNotificationMutation = useMutation(
    async (settings) => {
      const response = await api.put(`/calendar/events/${event.id}/notifications`, settings);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Notification settings updated');
        setIsEditingNotifications(false);
        onEventUpdate();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update notification settings');
      }
    }
  );

  const createEventMutation = useMutation(
    async (payload) => {
      const response = await api.post('/calendar/events', payload);
      return response.data.event;
    },
    {
      onSuccess: () => {
        toast.success('Meeting created');
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('upcomingEvents');
        queryClient.invalidateQueries('calendarStatus');
        onEventUpdate();
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create meeting');
      }
    }
  );

  const updateEventMutation = useMutation(
    async (payload) => {
      const response = await api.put(`/calendar/events/${event.id}`, payload);
      return response.data.event;
    },
    {
      onSuccess: () => {
        toast.success('Meeting updated');
        setIsEditingMeeting(false);
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('upcomingEvents');
        queryClient.invalidateQueries('calendarStatus');
        onEventUpdate();
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update meeting');
      }
    }
  );

  const handleSaveNotifications = () => {
    updateNotificationMutation.mutate(notificationSettings);
  };

  const handleCancelNotifications = () => {
    setNotificationSettings(normalizeNotificationSettings(event?.notificationSettings));
    setIsEditingNotifications(false);
  };

  const handleSaveMeeting = () => {
    if (!eventForm.title.trim()) {
      toast.error('Meeting title is required');
      return;
    }

    if (!eventForm.startTime || !eventForm.endTime) {
      toast.error('Start time and end time are required');
      return;
    }

    const startTime = new Date(eventForm.startTime);
    const endTime = new Date(eventForm.endTime);
    if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime()) || endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }

    const payload = {
      title: eventForm.title.trim(),
      description: eventForm.description.trim(),
      location: eventForm.location.trim(),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      provider: eventForm.provider,
      accountId: eventForm.accountId,
      calendarId: eventForm.calendarId
    };

    if (event) {
      updateEventMutation.mutate(payload);
      return;
    }

    if (!payload.provider || !payload.accountId || !payload.calendarId) {
      toast.error('Choose a calendar source before creating the meeting');
      return;
    }

    createEventMutation.mutate(payload);
  };

  const handleResetMeetingForm = () => {
    if (event) {
      setEventForm(buildEventFormFromEvent(event));
      setIsEditingMeeting(false);
      return;
    }

    setEventForm(buildDefaultEventForm(selectedDate, writableSources));
  };

  if (!isOpen) {
    return null;
  }

  const isSavingMeeting = createEventMutation.isLoading || updateEventMutation.isLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {event ? 'Event Details' : 'Create New Meeting'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {!event && !sourcesData?.hasWritableSource && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Reconnect a Google or Microsoft account with calendar write access before creating meetings.
            </div>
          )}

          {event && !canEditExistingMeeting && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Reconnect this {event.calendarProvider} account with calendar write access to edit meeting details.
            </div>
          )}

          {!event || isEditingMeeting ? (
            <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  {event ? 'Edit meeting' : 'New meeting'}
                </h3>
                {event && (
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-500 hover:text-gray-700"
                    onClick={handleResetMeetingForm}
                  >
                    Cancel
                  </button>
                )}
              </div>

              {!event && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Account</label>
                    <select
                      value={eventForm.accountId}
                      onChange={(e) => {
                        const source = writableSources.find((entry) => entry.accountId === e.target.value);
                        setEventForm((prev) => ({
                          ...prev,
                          accountId: source?.accountId || '',
                          provider: source?.provider || '',
                          calendarId: source?.calendars?.[0]?.id || ''
                        }));
                      }}
                      className="input-field"
                      disabled={isLoadingSources}
                    >
                      <option value="">Select an account</option>
                      {writableSources.map((source) => (
                        <option key={source.accountId} value={source.accountId}>
                          {source.provider} · {source.email || source.displayName || source.accountId}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Calendar</label>
                    <select
                      value={eventForm.calendarId}
                      onChange={(e) => setEventForm((prev) => ({ ...prev, calendarId: e.target.value }))}
                      className="input-field"
                      disabled={!selectedSource}
                    >
                      <option value="">Select a calendar</option>
                      {selectedSourceCalendars.map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
                <input
                  value={eventForm.title}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows="4"
                  className="input-field"
                  maxLength={5000}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Location</label>
                <input
                  value={eventForm.location}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, location: e.target.value }))}
                  className="input-field"
                  maxLength={300}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Start time</label>
                  <input
                    type="datetime-local"
                    value={eventForm.startTime}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, startTime: e.target.value }))}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">End time</label>
                  <input
                    type="datetime-local"
                    value={eventForm.endTime}
                    onChange={(e) => setEventForm((prev) => ({ ...prev, endTime: e.target.value }))}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleResetMeetingForm}
                  className="btn-secondary"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSaveMeeting}
                  disabled={isSavingMeeting || (!event && !sourcesData?.hasWritableSource)}
                  className="btn-primary flex items-center justify-center"
                >
                  {isSavingMeeting ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      {event ? <FiEdit className="mr-2 h-4 w-4" /> : <FiPlus className="mr-2 h-4 w-4" />}
                      {event ? 'Save meeting' : 'Create meeting'}
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                  {event.description && (
                    <p className="mt-1 text-gray-600">{event.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditingMeeting(true)}
                  disabled={!canEditExistingMeeting}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  Edit meeting
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <FiClock className="mr-2 h-4 w-4" />
                  <span>
                    {new Date(event.startTime).toLocaleString()} - {new Date(event.endTime).toLocaleString()}
                  </span>
                </div>

                {event.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <FiMapPin className="mr-2 h-4 w-4" />
                    <span>{event.location}</span>
                  </div>
                )}

                <div className="flex items-center text-sm text-gray-600">
                  <FiUser className="mr-2 h-4 w-4" />
                  <span className="capitalize">
                    {event.calendarProvider} Calendar
                    {currentSource?.email ? ` · ${currentSource.email}` : ''}
                  </span>
                </div>
              </div>

              {event.attendees && event.attendees.length > 0 && (
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-900">Attendees</h4>
                  <div className="space-y-1">
                    {event.attendees.map((attendee, index) => (
                      <div key={`${attendee.email || attendee.name}-${index}`} className="text-sm text-gray-600">
                        {attendee.name || attendee.email}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {event && (
            <div className="border-t border-gray-200 pt-6">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="flex items-center text-sm font-medium text-gray-900">
                  <FiBell className="mr-2 h-4 w-4" />
                  Notification Settings
                </h4>
                <button
                  onClick={() => setIsEditingNotifications((current) => !current)}
                  className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  <FiEdit className="mr-1 h-3 w-3" />
                  {isEditingNotifications ? 'Cancel' : 'Edit'}
                </button>
              </div>

              {isEditingNotifications ? (
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={notificationSettings.enabled}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            enabled: e.target.checked
                          }))
                        }
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable notifications</span>
                    </label>
                  </div>

                  {notificationSettings.enabled && (
                    <>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Notification Type
                        </label>
                        <select
                          value={notificationSettings.notificationType}
                          onChange={(e) =>
                            setNotificationSettings((prev) => {
                              const value = e.target.value;
                              const next = {
                                ...prev,
                                notificationType: value
                              };

                              if (!['call', 'both'].includes(value)) {
                                next.preCallSms = {
                                  ...(prev.preCallSms || {}),
                                  enabled: false,
                                  minutesBeforeCall: prev.preCallSms?.minutesBeforeCall ?? 15
                                };
                              }

                              return next;
                            })
                          }
                          className="input-field"
                        >
                          <option value="call">Phone Call</option>
                          <option value="sms">SMS</option>
                          <option value="both">Both</option>
                          <option value="none">None</option>
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Notification Time (minutes before event)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="120"
                          value={notificationSettings.notificationTime}
                          onChange={(e) => {
                            const minutes = Math.max(1, parseInt(e.target.value, 10) || 1);
                            setNotificationSettings((prev) => ({
                              ...prev,
                              notificationTime: minutes
                            }));
                          }}
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Custom Message (optional)
                        </label>
                        <textarea
                          value={notificationSettings.customMessage}
                          onChange={(e) =>
                            setNotificationSettings((prev) => ({
                              ...prev,
                              customMessage: e.target.value
                            }))
                          }
                          rows="3"
                          className="input-field"
                          placeholder="Leave empty to use default message"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            Pre-call SMS Reminder
                          </label>
                          <input
                            type="checkbox"
                            checked={notificationSettings.preCallSms?.enabled || false}
                            onChange={(e) =>
                              setNotificationSettings((prev) => ({
                                ...prev,
                                preCallSms: {
                                  ...(prev.preCallSms || {}),
                                  enabled: e.target.checked,
                                  minutesBeforeCall: prev.preCallSms?.minutesBeforeCall ?? 15
                                }
                              }))
                            }
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            disabled={!['call', 'both'].includes(notificationSettings.notificationType)}
                          />
                        </div>

                        {!['call', 'both'].includes(notificationSettings.notificationType) && (
                          <p className="text-xs text-gray-500">
                            Enable phone calls to configure pre-call SMS reminders.
                          </p>
                        )}

                        {notificationSettings.preCallSms?.enabled && (
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700">Minutes before call:</label>
                            <input
                              type="number"
                              min="1"
                              max="240"
                              value={notificationSettings.preCallSms?.minutesBeforeCall ?? 15}
                              onChange={(e) => {
                                const minutes = Math.max(1, parseInt(e.target.value, 10) || 1);
                                setNotificationSettings((prev) => ({
                                  ...prev,
                                  preCallSms: {
                                    ...(prev.preCallSms || {}),
                                    enabled: true,
                                    minutesBeforeCall: minutes
                                  }
                                }));
                              }}
                              className="input-field w-24"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleSaveNotifications}
                      disabled={updateNotificationMutation.isLoading}
                      className="btn-primary flex flex-1 items-center justify-center"
                    >
                      {updateNotificationMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelNotifications}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Status:</span>{' '}
                    {notificationSettings.enabled ? 'Enabled' : 'Disabled'}
                  </div>
                  {notificationSettings.enabled && (
                    <>
                      <div>
                        <span className="font-medium">Type:</span>{' '}
                        {notificationSettings.notificationType === 'call' && 'Phone Call'}
                        {notificationSettings.notificationType === 'sms' && 'SMS'}
                        {notificationSettings.notificationType === 'both' && 'Both'}
                        {notificationSettings.notificationType === 'none' && 'None'}
                      </div>
                      <div>
                        <span className="font-medium">Time:</span>{' '}
                        {notificationSettings.notificationTime} minutes before
                      </div>
                      {notificationSettings.customMessage && (
                        <div>
                          <span className="font-medium">Custom Message:</span>{' '}
                          {notificationSettings.customMessage}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Pre-call SMS:</span>{' '}
                        {notificationSettings.preCallSms?.enabled
                          ? `${notificationSettings.preCallSms.minutesBeforeCall ?? 15} minutes before call`
                          : 'Disabled'}
                      </div>
                    </>
                  )}
                </div>
              )}

              {event.notificationsSent && event.notificationsSent.length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Recent Notifications</h4>
                  <div className="space-y-3">
                    {[...event.notificationsSent]
                      .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))
                      .map((notification, index) => {
                        const typeLabels = {
                          call: 'Call',
                          sms: 'SMS',
                          sms_pre: 'SMS (pre-call)'
                        };

                        return (
                          <div
                            key={`${notification.type}-${notification.sentAt}-${index}`}
                            className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2"
                          >
                            <div className="text-sm">
                              <span className="font-semibold text-gray-800">
                                {typeLabels[notification.type] || notification.type.toUpperCase()}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                {new Date(notification.sentAt).toLocaleString()}
                              </span>
                              {notification.errorMessage && (
                                <p className="mt-1 text-xs text-red-600">{notification.errorMessage}</p>
                              )}
                            </div>
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                notification.status === 'success'
                                  ? 'bg-green-100 text-green-700'
                                  : notification.status === 'failed'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {notification.status}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventModal;
